import React, { useEffect, useMemo, useState } from 'react';

import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import ProductPerformanceTrend from './ProductPerformanceTrend';
import { buildWeeklyOption } from './charts';
import type { ProductPoint } from './model';

type Props = {
  title: string;
  points: Array<ProductPoint>;
  source: string;
  range: string;
  onSelect: (value: string) => void;
};

/** Every source member remains accessible; small multiples keep each member's trend readable. */
export default function ProductPerformanceWeekly({ title, points, source, range, onSelect }: Props) {
  const theme = useTheme();
  const [count, setCount] = useState(12);
  useEffect(() => setCount(12), [points]);
  const groups = useMemo(() => {
    const byName = new Map<string, Array<ProductPoint>>();
    for (const point of points) {
      const group = byName.get(point.product) ?? [];
      group.push(point);
      byName.set(point.product, group);
    }
    return [...byName].sort(([left], [right]) => left.localeCompare(right));
  }, [points]);
  return (
    <Stack gap={1.5} sx={{ minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Box>
          <Typography component="h2" variant="h6">
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Showing {Math.min(count, groups.length)} of {groups.length} source members · Select a series or use its
            filter
          </Typography>
        </Box>
        {groups.length > 12 && (
          <Button size="small" onClick={() => setCount(count >= groups.length ? 12 : groups.length)}>
            {count >= groups.length ? 'Show first 12' : `Show all ${groups.length}`}
          </Button>
        )}
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
          gap: 1.5,
          minWidth: 0,
        }}
      >
        {groups.slice(0, count).map(([name, series]) => (
          <ProductPerformanceTrend
            key={name}
            title={name}
            source={source}
            range={range}
            height={250}
            option={buildWeeklyOption(series, theme.palette.mode === 'dark')}
            onSelect={() => onSelect(name)}
          />
        ))}
      </Box>
      {!groups.length && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">
            No weekly members match these filters. Clear a selection or change the dates.
          </Typography>
        </Paper>
      )}
    </Stack>
  );
}
